import React, { useState, useEffect, useCallback } from 'react';
import {
  Box,
  Typography,
  Button,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  IconButton,
  Tooltip,
  CircularProgress,
  Alert,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  FormControlLabel,
  Switch
} from '@mui/material';
import {
  PersonAdd as PersonAddIcon,
  Delete as DeleteIcon,
  AdminPanelSettings as AdminIcon,
  Visibility as ViewIcon,
  VisibilityOff as HideIcon,
  Restore as RestoreIcon
} from '@mui/icons-material';
import api from '../../utils/api';
import AssignUserDialog from './AssignUserDialog';

const BranchUserManagement = ({ branchId, branchName }) => {
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [assignDialogOpen, setAssignDialogOpen] = useState(false);
  const [selectedUser, setSelectedUser] = useState(null);
  const [confirmDialogOpen, setConfirmDialogOpen] = useState(false);
  const [roleDialogOpen, setRoleDialogOpen] = useState(false);
  const [showInactive, setShowInactive] = useState(false);

  const fetchUsers = useCallback(async () => {
    try {
      const queryParam = showInactive ? '?include_inactive=true' : '';
      const response = await api.get(`/user-branches/branch/${branchId}/users${queryParam}`);
      setUsers(response.data.users);
      setLoading(false);
    } catch (err) {
      setError(err.response?.data?.message || 'Failed to load users');
      setLoading(false);
    }
  }, [branchId, showInactive]);

  useEffect(() => {
    fetchUsers();
  }, [fetchUsers]);

  const handleAssignUser = async (userId, isBranchAdmin) => {
    try {
      await api.post('/user-branches/assign', {
        userId,
        branchId,
        isBranchAdmin
      });
      fetchUsers();
      setAssignDialogOpen(false);
    } catch (err) {
      setError(err.response?.data?.message || 'Failed to assign user');
    }
  };

  const handleRemoveUser = async () => {
    try {
      await api.delete(`/user-branches/${branchId}/users/${selectedUser.id}`);
      fetchUsers();
      setConfirmDialogOpen(false);
      setSelectedUser(null);
    } catch (err) {
      setError(err.response?.data?.message || 'Failed to remove user');
    }
  };

  const handleUpdateRole = async (isBranchAdmin) => {
    try {
      await api.put(`/user-branches/${branchId}/users/${selectedUser.id}/role`, {
        isBranchAdmin
      });
      fetchUsers();
      setRoleDialogOpen(false);
      setSelectedUser(null);
    } catch (err) {
      setError(err.response?.data?.message || 'Failed to update user role');
    }
  };

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', p: 3 }}>
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Box sx={{ p: 3 }}>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
        <Typography variant="h5">
          Users in {branchName}
        </Typography>
        <Box sx={{ display: 'flex', gap: 2, alignItems: 'center' }}>
          <Button
            variant="outlined"
            startIcon={showInactive ? <HideIcon /> : <ViewIcon />}
            onClick={() => setShowInactive(!showInactive)}
            size="small"
          >
            {showInactive ? 'Hide Removed' : 'Show Removed'}
          </Button>
          <Button
            variant="contained"
            startIcon={<PersonAddIcon />}
            onClick={() => setAssignDialogOpen(true)}
          >
            Assign User
          </Button>
        </Box>
      </Box>

      {error && (
        <Alert severity="error" sx={{ mb: 3 }}>
          {error}
        </Alert>
      )}

      <TableContainer component={Paper} variant="outlined">
        <Table>
          <TableHead>
            <TableRow>
              <TableCell>Name</TableCell>
              <TableCell>Email</TableCell>
              <TableCell>Role</TableCell>
              <TableCell>Branch Role</TableCell>
              <TableCell>Status</TableCell>
              <TableCell align="right">Actions</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {users.map((user) => (
              <TableRow key={user.id}>
                <TableCell>
                  {user.first_name} {user.last_name}
                </TableCell>
                <TableCell>{user.email}</TableCell>
                <TableCell>{user.role}</TableCell>
                <TableCell>
                  {user.is_branch_admin ? 'Branch Admin' : 'Staff'}
                </TableCell>
                <TableCell>
                  <Box
                    sx={{
                      bgcolor: user.status === 'active' ? 'success.light' : 'error.light',
                      color: 'white',
                      px: 1,
                      py: 0.5,
                      borderRadius: 1,
                      display: 'inline-block'
                    }}
                  >
                    {user.status}
                  </Box>
                </TableCell>
                <TableCell align="right">
                  {user.status === 'active' ? (
                    <>
                      <Tooltip title="Update Role">
                        <IconButton
                          onClick={() => {
                            setSelectedUser(user);
                            setRoleDialogOpen(true);
                          }}
                          size="small"
                        >
                          <AdminIcon />
                        </IconButton>
                      </Tooltip>
                      <Tooltip title="Remove User">
                        <IconButton
                          onClick={() => {
                            setSelectedUser(user);
                            setConfirmDialogOpen(true);
                          }}
                          size="small"
                          color="error"
                        >
                          <DeleteIcon />
                        </IconButton>
                      </Tooltip>
                    </>
                  ) : (
                    <Tooltip title="Re-assign User">
                      <IconButton
                        onClick={() => handleAssignUser(user.id, user.is_branch_admin)}
                        size="small"
                        color="primary"
                      >
                        <RestoreIcon />
                      </IconButton>
                    </Tooltip>
                  )}
                </TableCell>
              </TableRow>
            ))}
            {users.length === 0 && (
              <TableRow>
                <TableCell colSpan={6} align="center">
                  <Typography color="textSecondary">
                    No users assigned to this branch
                  </Typography>
                </TableCell>
              </TableRow>
            )}
          </TableBody>
        </Table>
      </TableContainer>

      <AssignUserDialog
        open={assignDialogOpen}
        onClose={() => setAssignDialogOpen(false)}
        onAssign={handleAssignUser}
        branchId={branchId}
      />

      {/* Confirm Remove Dialog */}
      <Dialog
        open={confirmDialogOpen}
        onClose={() => setConfirmDialogOpen(false)}
      >
        <DialogTitle>Remove User from Branch</DialogTitle>
        <DialogContent>
          <Typography>
            Are you sure you want to remove {selectedUser?.first_name} {selectedUser?.last_name} from this branch?
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setConfirmDialogOpen(false)}>Cancel</Button>
          <Button onClick={handleRemoveUser} color="error" variant="contained">
            Remove
          </Button>
        </DialogActions>
      </Dialog>

      {/* Update Role Dialog */}
      <Dialog
        open={roleDialogOpen}
        onClose={() => setRoleDialogOpen(false)}
      >
        <DialogTitle>Update Branch Role</DialogTitle>
        <DialogContent>
          <Box sx={{ pt: 2 }}>
            <FormControlLabel
              control={
                <Switch
                  checked={selectedUser?.is_branch_admin || false}
                  onChange={(e) => handleUpdateRole(e.target.checked)}
                />
              }
              label="Branch Admin"
            />
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setRoleDialogOpen(false)}>Cancel</Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default BranchUserManagement; 